<template>
  <header
    id="header"
    :class="['app-header', { 'app-header--inverted': inverted }]"
    :style="componentStyle"
  >
    <div class="nav-products nav-products-full display-none tablet:display-block">
      <page-header-products />
    </div>

    <div
      id="nav-app"
      class="nav-app padding-y-md"
    >
      <div class="container display-flex flex-content-between flex-nowrap">
        <div
          class="display-flex"
          style="gap: var(--wa-space-2xs)"
        >
          <!-- Nav toggle -->
          <button
            class="flat swap-icons header-toggle-nav tablet:display-none"
            aria-label="Open Main Menu"
            data-balloon-pos="right"
            data-drawer="open app-drawer"
          >
            <font-awesome-icon
              :icon="$v7.regular.faBars"
              size="xl"
              class="initial-icon"
            />
            <font-awesome-icon
              :icon="$v7.solid.faHamburger"
              size="xl"
              class="hover-icon"
            />
          </button>

          <div class="header-nav display-none tablet:display-flex flex-items-center tablet:flex-items-stretch">
            <div
              class="wrap-links display-flex flex-items-center"
              style="gap: var(--spacing-sm)"
            >
              <site-link
                v-if="$account"
                :to="routes.kits.location"
                :class="mainLink(routes.kits)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'kits' })"
              >
                <span class="display-none laptop:display-inline">Your </span>Kits
              </site-link>
              <site-link
                v-else
                :to="routes.start.location"
                :class="mainLink(routes.start)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'start' })"
              >
                Start
              </site-link>

              <site-link
                :to="routes.search.location"
                :class="mainLink(routes.search)"
                title="Search icons"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'icon_search' })"
              >
                <font-awesome-icon :icon="$v7.solid.faMagnifyingGlass" />
                <span class="sr-only">Search Icons</span>
              </site-link>

              <site-link
                :to="routes.icons.location"
                :class="mainLink(routes.icons)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'icons' })"
              >
                Icons
              </site-link>

              <site-link
                :to="routes.iconwizard.location"
                :class="mainLink(routes.iconwizard)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'icon_wizard' })"
              >
                Wizard
              </site-link>

              <site-link
                :to="routes.ai.location"
                :class="mainLink(routes.ai)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'ai' })"
              >
                AI
              </site-link>

              <site-link
                :to="routes.docs.location"
                :class="mainLink(routes.docs)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'docs' })"
              >
                Docs
              </site-link>

              <site-link
                :to="routes.plans.location"
                :class="mainLink(routes.plans)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'plans' })"
              >
                Plans
              </site-link>

              <site-link
                :to="routes.support.location"
                :class="mainLink(routes.support)"
                @click="$posthog.capture('main_navigation_link_click', { destination: 'support' })"
              >
                Support
              </site-link>
            </div>
          </div>
        </div>

        <!-- Simple Logo -->
        <site-link
          :to="{ name: 'home' }"
          class="button flat tablet:display-none"
          aria-label="Font Awesome"
          @click="$posthog.capture('main_navigation_link_click', { destination: 'home' })"
        >
          <owa-logo-flag logo-size="2xl" />
        </site-link>

        <!-- Account -->
        <div :style="inverted ? null : { '--button-hover-background': 'var(--fa-lt-gravy)' }">
          <page-header-account-menu v-if="$account" />
          <site-link
            v-else
            :to="routes.signIn.location"
            :class="['button flat swap-icons', { active: routes.signIn.active }]"
            aria-label="Sign In"
            @click="$posthog.capture('main_navigation_link_click', { destination: 'sign_in' })"
          >
            <span
              class="display-none laptop:display-inline"
              style="font-weight: normal"
              >Sign In</span
            >
            <font-awesome-icon
              :icon="$v7.regular.faArrowRightToBracket"
              size="xl"
              class="initial-icon margin-left-4xs"
            />
            <font-awesome-icon
              :icon="$v7.regular.faPersonToPortal"
              size="xl"
              class="hover-icon margin-left-4xs"
              style="right: 0; left: initial"
            />
          </site-link>
        </div>
      </div>
    </div>

    <page-header-drawer />
  </header>
</template>

<script>
  import PageHeaderAccountMenu from './PageHeaderAccountMenu'
  import PageHeaderDrawer from './PageHeaderDrawer'
  import PageHeaderProducts from './PageHeaderProducts'
  import { PageHeaderRoutes } from './mixins'

  export default {
    name: 'PageHeader',

    components: {
      PageHeaderAccountMenu,
      PageHeaderDrawer,
      PageHeaderProducts
    },

    mixins: [PageHeaderRoutes],

    props: {
      customStyle: {
        type: Object,
        default: () => {}
      },
      themeColor: {
        type: String
      },
      inverted: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      componentStyle() {
        const style = { ...this.customStyle }
        if (this.themeColor) {
          style['--link-active-color'] = this.themeColor
        }
        return style
      }
    },

    // Replaces the retired `owa-force-update-on-mounted` wrapper. That was a
    // Vue 2 functional component reaching `parent.$once('hook:mounted')` --
    // `$once`, `hook:` events and the functional render context are all gone in
    // Vue 3, and because it wrapped the whole header its error took the app
    // down. The behaviour it provided is this one hook.
    mounted() {
      this.$forceUpdate()
    },

    methods: {
      mainLink(route) {
        return ['no-underline padding-y-xs padding-x-md', { active: route.active }]
      }
    }
  }
</script>
